import { Device, DeviceInfo } from '@capacitor/device'
import { isPlatformNative, getSourceType } from '../../../utilities/platform.util'
import { IDeviceDetails } from '../store/login.types'
import { getDateTime } from '../../../utilities/dateTime.util'

type DeviceInfoWithName = (DeviceInfo & { name?: string }) | null

export class DeviceDetailsService {
  async getDeviceDetails(): Promise<IDeviceDetails> {
    return isPlatformNative() ? this.getNativeDeviceDetails() : this.getPwaDeviceDetails()
  }

  private async getNativeDeviceDetails(): Promise<IDeviceDetails> {
    const [deviceInfo, deviceIdResult] = await Promise.all([
      Device.getInfo().catch((): DeviceInfo | null => null),
      Device.getId().catch(() => null),
    ])

    let deviceId: string
    if (deviceIdResult?.identifier) {
      deviceId = deviceIdResult.identifier
    } else {
      const { DeviceInfoService } = await import('../../../services/deviceInfo.service')
      deviceId = (await new DeviceInfoService().createOrGet()).uuid
    }

    return this.buildDetails(deviceId, deviceInfo, getDateTime())
  }

  private async getPwaDeviceDetails(): Promise<IDeviceDetails> {
    const [deviceInfo, storedDeviceInfo] = await Promise.all([
      Device.getInfo().catch((): DeviceInfo | null => null),
      import('../../../services/deviceInfo.service').then(({ DeviceInfoService }) =>
        new DeviceInfoService().createOrGet()
      ),
    ])

    return this.buildDetails(storedDeviceInfo.uuid, deviceInfo, storedDeviceInfo.createDateTime)
  }

  private buildDetails(deviceId: string, deviceInfo: DeviceInfo | null, created: string): IDeviceDetails {
    return {
      deviceId,
      deviceType: deviceInfo?.operatingSystem ?? 'unknown',
      deviceName: (deviceInfo as DeviceInfoWithName)?.name || deviceInfo?.model || 'unknown',
      userAgent: navigator.userAgent,
      sourceType: getSourceType(),
      screen: `${window.screen.width}x${window.screen.height}`,
      created,
    }
  }
}
